import { DepthTexture, HalfFloatType, RenderTarget as ThreeRenderTarget, type Texture, UnsignedByteType } from "three/webgpu";
import type { Color } from "../common/color.js";
import type { GpuContext } from "./GpuContext.js";
import { configureTexture, type DrawableTexture } from "./texture.js";

export interface RenderTargetOptions {
  width: number;
  height: number;
  /** Allocate a depth buffer. 3D needs one; 2D sorts by hand and does not. */
  depth?: boolean;
  /** Sample it smoothly when the UI draws it. Off for pixel art. */
  smooth?: boolean;
  /**
   * Let the depth attachment be read as a texture (`depthTexture`). Post effects that reconstruct a world
   * position from depth need it.
   */
  sampleDepth?: boolean;
  /**
   * `"display"` (8 bits a channel, the default) holds values ready to show. `"linear"` is half-float, for a lit
   * scene that accumulates light past 1 before it is tone mapped and encoded for display.
   */
  precision?: "display" | "linear";
  /** What is rendered into it comes out premultiplied - a scene blended over a transparent clear. */
  premultiplied?: boolean;
  label?: string;
}

/**
 * An off-screen surface a scene renders into, which the UI then draws as an ordinary texture.
 *
 * This is the seam between a game's scenes and the windowing front-end, and it is what keeps them from
 * having to know about each other: the scene renders into a target, and the result reaches a window through
 * `rc.drawTexture` like any bitmap - so a viewport clips, scrolls, sits inside a window and obeys z-order for
 * free.
 */
export class RenderTarget implements DrawableTexture {
  readonly gpu: GpuContext;
  readonly target: ThreeRenderTarget;
  readonly premultiplied: boolean;

  #destroyed = false;

  constructor(gpu: GpuContext, options: RenderTargetOptions) {
    this.gpu = gpu;
    this.premultiplied = options.premultiplied ?? false;
    const width = Math.max(1, Math.floor(options.width));
    const height = Math.max(1, Math.floor(options.height));
    const sampleDepth = options.sampleDepth ?? false;
    this.target = new ThreeRenderTarget(width, height, {
      type: options.precision === "linear" ? HalfFloatType : UnsignedByteType,
      depthBuffer: (options.depth ?? false) || sampleDepth,
      depthTexture: sampleDepth ? new DepthTexture(width, height) : null,
      generateMipmaps: false,
    });
    configureTexture(this.target.texture, options.smooth ?? true);
    this.target.texture.name = options.label ?? "webwin.target";
  }

  get width(): number {
    return this.target.width;
  }

  get height(): number {
    return this.target.height;
  }

  /** What `rc.drawTexture` samples. */
  get texture(): Texture {
    return this.target.texture;
  }

  /** The depth attachment as a texture, or null when it was not made sampleable. */
  get depthTexture(): DepthTexture | null {
    return this.target.depthTexture;
  }

  /**
   * Match a new size, reallocating only when it actually changed. A viewport calls this every frame from its
   * paint, so the early-out is the point.
   */
  resize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    if (w === this.target.width && h === this.target.height) return;
    this.target.setSize(w, h);
  }

  /** Render into this target from now on, cleared to `clear` (kept as it was when null). */
  begin(clear: Color | null): void {
    this.gpu.beginFrame(clear ?? null, this.target);
  }

  dispose(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.target.dispose();
  }
}
